import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { Sort } from '@angular/material/sort';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import { PageParams } from 'src/app/core/models/page-params.model';
import { formConstant } from '../../../../../../config';
import { IConstantItem } from '../../../../../core/models/available-types.interface';
import { ICountry } from '../../../../../core/models/country.interface';
import { IPaginatorProperties } from '../../../../../core/models/paginator.interface';
import { NotificationService } from '../../../../../core/services/notification.service';
import { UserService } from '../../../../../core/services/user.service';
import { ISPItemSizeRestriction, ISubCarrierPriceError } from '../../../models/shipment-price.interface';
import { ShipmentPriceService } from '../../../services/shipment-price.service';

@Component({
    selector: 'eci-shipment-price-item-size-restrictions',
    templateUrl: './item-size-restrictions.component.html',
    styleUrls: [ './item-size-restrictions.component.scss' ]
})
export class ItemSizeRestrictionsComponent implements OnInit {
    public form: FormGroup;

    @ViewChild(MatTable) public table: MatTable<ISPItemSizeRestriction>;

    @Input() theSPItemSizeRestrictions: ISPItemSizeRestriction[];
    @Input() shipmentPriceItemId: number;
    @Input() theCountriesList: ICountry[];

    showLoader: boolean = false;
    public currentSort: Sort = {active: 'createdAt', direction: 'asc'};
    public pageParams: PageParams = new PageParams();

    public dimensionsUnits: IConstantItem[] = [
        {id: 0, name: 'mm'},
        {id: 1, name: 'cm'},
        {id: 2, name: 'm'},
        {id: 3, name: 'dm'},
        {id: 4, name: 'km'},
    ];
    public weightUnits: IConstantItem[] = [
        {id: 0, name: 'g'},
        {id: 1, name: 'kg'},
        {id: 2, name: 't'},
    ];
    public volumeUnits: IConstantItem[] = [
        {id: 1, name: 'liters'},
        {id: 2, name: 'mm3'},
        {id: 3, name: 'cm3'},
        {id: 4, name: 'm3'},
    ];
    // quantityUnits: IConstantItem[];

    public dataSource: MatTableDataSource<ISPItemSizeRestriction>;
    public displayedColumns: string[] = [
        'packingType',
        'countryCode',
        'maxDims',
        'dimensionsUnitsOfMeasure',
        'maxWeight',
        'weightUnitsOfMeasure',
        'maxVolume',
        'volumeUnitsOfMeasure',
        'maxQuantity',
        'action',
    ];

    constructor(
        public router: Router,
        private userSvc: UserService,
        private notify: NotificationService,
        private shipmentPriceService: ShipmentPriceService,
    ) {
    }

    private getModel(data: { form: any; element: ISPItemSizeRestriction }): ISPItemSizeRestriction {
        const restriction = data.element;
        // restriction.parentId = 0;
        restriction.packingType = data.form.packingType;
        restriction.countryCode = data.form.countryCode;
        restriction.maxWidth = data.form.maxWidth;
        restriction.maxHeight = data.form.maxHeight;
        restriction.maxLength = data.form.maxLength;
        restriction.dimensionsUnitsOfMeasure = data.form.dimensionsUnitsOfMeasure;
        restriction.maxWeight = data.form.maxWeight;
        restriction.weightUnitsOfMeasure = data.form.weightUnitsOfMeasure;
        restriction.maxVolume = data.form.maxVolume;
        restriction.volumeUnitsOfMeasure = data.form.volumeUnitsOfMeasure;
        restriction.maxQuantity = data.form.maxQuantity;
        restriction.createdBy = this.userSvc.user.email;
        restriction.shipmentPriceItemId = this.shipmentPriceItemId;
        return restriction;
    }

    private onResponseSuccess(element: ISPItemSizeRestriction, onSuccess: any, index: number ): void {
        this.showLoader = false;
        onSuccess();
        this.theSPItemSizeRestrictions[index] = element;
        this.dataSource.data = this.theSPItemSizeRestrictions;
    }

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource<ISPItemSizeRestriction>(this.theSPItemSizeRestrictions);
        this.pageParams.pageSize = formConstant.pageSize;
        this.pageParams.pageIndex = 0;
        this.pageParams.length = this.dataSource.data.length;
        if (this.shipmentPriceItemId && !this.theSPItemSizeRestrictions) {
            this.getList();
        }
    }

    pageChanged(e: PageParams): void {
        this.pageParams.pageIndex = e.pageIndex;
        this.pageParams.pageSize = e.pageSize;
        this.getList();
    }

    public get isAdmin(): boolean {
        return this.userSvc.isAdmin;
    }

    public addNewRow(event): void {
        if (!this.dataSource?.data) {
            this.dataSource = new MatTableDataSource<ISPItemSizeRestriction>([ event ]);
            this.shipmentPriceItemId = event.shipmentPriceItemId;
        }

        this.theSPItemSizeRestrictions.push(event);
        this.dataSource.data = this.theSPItemSizeRestrictions;
    }

    public removeData(index: number): void {
        this.theSPItemSizeRestrictions.splice(index, 1);
        this.dataSource.data = this.theSPItemSizeRestrictions;
    }

    public save(data: { form: any; element: ISPItemSizeRestriction, onSuccess: any, index: number }): void {
        if (data.element.id) {
            this.update(data);
            return;
        }
        this.showLoader = true;
        this.shipmentPriceService.create<ISPItemSizeRestriction>('restrictions', this.getModel(data))
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Created successfully`);
                this.onResponseSuccess(p, data.onSuccess, data.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    public update(data: { form: any; element: ISPItemSizeRestriction, onSuccess: any, index: number }): void {
        this.showLoader = true;
        this.shipmentPriceService.update<ISPItemSizeRestriction>('restrictions', data.element.id, this.getModel(data))
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Updated successfully`);
                this.onResponseSuccess(p, data.onSuccess, data.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    public delete(element: { id: number, index: number }): void {
        this.showLoader = true;
        this.shipmentPriceService.delete('restrictions', element.id)
            .subscribe((p) => {
                this.notify.showSuccessMessage(`Deleted successfully`);
                this.removeData(element.index);
            }, (err) => {
                this.notify.showError(err.error.message);
                this.showLoader = false;
            });
    }

    private getList(page: number = this.pageParams.pageIndex + 1 || 1,
                    limit: number = this.pageParams.pageSize,
                    q: string = '',
                    filters: any = {},
                    orderField: string = this.currentSort?.active || '',
                    orderDir: string = this.currentSort?.direction.toUpperCase() || '') {
        this.showLoader = true;
        this.shipmentPriceService.getRestrictionsListByPriceItemId(
            this.shipmentPriceItemId, page, limit, q, filters, orderField, orderDir
        ).subscribe((data) => {
                if (!data) {
                    return;
                }
                this.theSPItemSizeRestrictions = data.items;
                this.dataSource = new MatTableDataSource<ISPItemSizeRestriction>(data.items);
                this.pageParams.pageSize = data.meta.itemsPerPage;
                this.pageParams.pageIndex = data.meta.currentPage - 1;
                this.pageParams.length = data.meta.totalItems;
            }, (err) => {
                this.notify.showError(err.error.message);
            }, () => {
                this.showLoader = false;
            }
        );
    }

    sortData(sort: Sort): void {
        const data = this.dataSource;
        if (!sort.active || sort.direction === '') {
            this.dataSource = data;
            return;
        }
        this.currentSort = sort;
        this.getList();
    }
}
